"use client"

import clsx from "clsx"
import React, { memo, useCallback, useEffect, useRef, useState } from "react"
import { useDataAttributes } from "../../hooks"
import { BaseInputProps, splitAriaProps } from "../../utils"

/**
 * @description 숫자 입력 필드. +/- 증감 버튼, min/max 범위 제한 지원
 * 참고: aria-*, role은 내부 input 요소에 적용됩니다. data-* 등 나머지 네이티브 속성은 래퍼 div에 적용됩니다.
 * @example
 * <TNumberInput value={50} min={0} max={100} step={1} onChange={setValue} />
 */
export interface TNumberInputProps extends Omit<BaseInputProps, "name"> {
	/** 입력 필드 name 속성 */
	name?: string
	/** 현재 숫자 값 */
	value?: number
	/** 최솟값 제한 */
	min?: number
	/** 최댓값 제한 */
	max?: number
	/**
	 * 증감 단위
	 * @default 1
	 */
	step?: number
	/**
	 * 입력 필드 크기
	 * @ai-style small=32px높이, medium=40px높이, large=48px높이
	 * @default "medium"
	 */
	size?: "small" | "medium" | "large"
	/** 입력 필드 앞에 표시할 콘텐츠 (아이콘, 단위 등) */
	prefix?: React.ReactNode
	/** 입력 필드 뒤에 표시할 콘텐츠 (아이콘, 단위 등) */
	suffix?: React.ReactNode
	/** 입력 필드 대신 표시할 커스텀 렌더링 콘텐츠 */
	renderValue?: React.ReactNode
	/**
	 * 정수만 허용 여부
	 * @default false
	 */
	integerOnly?: boolean
	/** 값 변경 콜백. 순수 숫자 값 전달 (이벤트 객체가 아님) */
	onChange?: (value: number) => void
	/** 포커스 이벤트 콜백 */
	onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void
	/** 포커스 해제 콜백 */
	onBlur?: () => void
	/** 키 입력 콜백 */
	onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
}

export const TNumberInput = memo(
	React.forwardRef<HTMLInputElement, TNumberInputProps>((props, ref) => {
		const {
			id,
			name,
			value,
			min,
			max,
			step = 1,
			size = "medium",
			placeholder,
			prefix,
			suffix,
			renderValue,
			integerOnly = false,
			required,
			disabled,
			error,
			readOnly,
			autoFocus,
			className,
			dataAttributes,
			onChange,
			onFocus,
			onBlur,
			onKeyDown,
			...restProps
		} = props

		const [inputValue, setInputValue] = useState<string>(value !== undefined ? value.toString() : "")
		const [isFocused, setIsFocused] = useState(false)
		const dataProps = useDataAttributes(dataAttributes)
		const { ariaProps, rest } = splitAriaProps(restProps)

		useEffect(() => {
			// Only sync external value to input when not focused
			if (!isFocused) {
				setInputValue(value !== undefined ? value.toString() : "")
			}
		}, [value, isFocused])

		const handleChange = React.useCallback(
			(event: React.ChangeEvent<HTMLInputElement>) => {
				const rawValue = event.target.value
				const regex = integerOnly ? /^-?\d*$/ : /^-?\d*\.?\d*$/
				if (rawValue === "" || rawValue === "-" || regex.test(rawValue)) {
					setInputValue(rawValue)
				}
			},
			[integerOnly]
		)

		const applyValue = useCallback(() => {
			let numValue = integerOnly ? parseInt(inputValue, 10) : parseFloat(inputValue)
			if (isNaN(numValue)) {
				numValue = min !== undefined ? min : 0
			}
			if (min !== undefined) {
				numValue = Math.max(min, numValue)
			}
			if (max !== undefined) {
				numValue = Math.min(max, numValue)
			}
			if (integerOnly) {
				numValue = Math.round(numValue)
			}
			const finalValueStr = numValue.toString()
			setInputValue(finalValueStr)
			if (onChange && (value === undefined || parseFloat(value.toString()) !== numValue)) {
				onChange(numValue)
			}
		}, [inputValue, min, max, value, onChange, integerOnly])

		const handleFocus = useCallback(
			(event: React.FocusEvent<HTMLInputElement>) => {
				setIsFocused(true)
				onFocus?.(event)
			},
			[onFocus]
		)

		// Enter 확정이 applyValue를 직접 호출했음을 표시한다. 뒤이은 .blur()의 handleBlur가 같은 값으로
		// applyValue를 재호출해 onChange가 2회 발화(이중 커밋)되는 것을 막는 가드.
		const justAppliedRef = useRef(false)

		const handleBlur = React.useCallback(() => {
			setIsFocused(false)
			// Enter 확정이 이미 applyValue를 호출했다면(justAppliedRef) 재호출하지 않는다.
			if (justAppliedRef.current) {
				justAppliedRef.current = false
			} else {
				applyValue()
			}
			onBlur?.()
		}, [applyValue, onBlur])

		const handleKeyDown = useCallback(
			(event: React.KeyboardEvent<HTMLInputElement>) => {
				if (event.key === "Enter") {
					// applyValue를 blur 이전(입력이 아직 포커스된 상태)에 호출한다 — ConfirmNumberInput 등 래퍼가
					// onChange 시점의 document.activeElement === input 여부로 포커스 유지를 판정하므로 이 순서를 보존한다.
					// 이어지는 .blur()의 handleBlur는 justAppliedRef 가드로 applyValue를 재호출하지 않으므로
					// onChange는 1회만 발화된다(이중 커밋 → undo no-op 방지).
					justAppliedRef.current = true
					applyValue()
					event.currentTarget.blur()
				}
				onKeyDown?.(event)
			},
			[applyValue, onKeyDown]
		)

		return (
			<div className={clsx("TInput", "number", size, className)} {...rest} data-disabled={disabled} data-readonly={readOnly} data-invalid={error} {...dataProps}>
				<div className="flex items-center flex-1">
					{prefix && <div className="prefix">{prefix}</div>}
					{renderValue ? (
						<div className="render-value">{renderValue}</div>
					) : (
						<input
							ref={ref}
							id={id}
							type="number"
							name={name}
							value={inputValue}
							placeholder={placeholder}
							required={required}
							disabled={disabled}
							readOnly={readOnly}
							{...ariaProps}
							aria-invalid={error}
							aria-required={required}
							autoFocus={autoFocus}
							step={step}
							min={min}
							max={max}
							onChange={handleChange}
							onFocus={handleFocus}
							onBlur={handleBlur}
							onKeyDown={handleKeyDown}
							{...dataProps}
						/>
					)}
					{suffix && <div className="suffix">{suffix}</div>}
				</div>
			</div>
		)
	})
)

TNumberInput.displayName = "TNumberInput"
